import { ColumnDef } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { Property } from "../rest/responses/Property";
import { DragEndEvent, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";

interface UseDraggableColumnsProps {
	columns: ColumnDef<Property, any>[];
	onColumnOrderChange?: (columnOrder: string[]) => void;
}

export const persistentColumns = ["selection", "link"];

export default function useDraggableColumns({ columns, onColumnOrderChange }: UseDraggableColumnsProps) {
	const [columnOrder, setColumnOrder] = useState<string[]>(() => {
		return columns.map((c) => c.id!);
	});

	const sensors = useSensors(useSensor(MouseSensor, {}), useSensor(TouchSensor, {}), useSensor(KeyboardSensor, {}));

	const onDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		if (active && over && active.id !== over.id) {
			if (persistentColumns.includes(active.id as string) || persistentColumns.includes(over.id as string)) {
				return;
			}

			setColumnOrder((columnOrder) => {
				const oldIndex = columnOrder.indexOf(active.id as string);
				const newIndex = columnOrder.indexOf(over.id as string);
				const changedOrder = arrayMove(columnOrder, oldIndex, newIndex); //this is just a splice util
				onColumnOrderChange?.(changedOrder);

				return changedOrder;
			});
		}
	};

	useEffect(() => {
		const existing = columnOrder;
		const incoming = columns.map((column) => column.id);

		const remove = existing.filter((id) => !incoming.includes(id));
		const add = incoming.filter((id) => !existing.includes(id!));

		const updated = [...existing.filter((id) => !remove.includes(id)), ...add] as string[];

		setColumnOrder(updated);
		// We don't want to include columnOrder here as it gets set within the body.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [columns]);

	return { columnOrder, setColumnOrder, sensors, onDragEnd };
}
